import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { formatApiError, LOGO_STACKED } from "../lib/api";

const BG_IMAGE = "/images/branding/pastor-worship.jpg";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const from = loc.state?.from?.pathname || "/members";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      nav(from, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="login-page" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background */}
      <div
        className="absolute inset-0 z-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${BG_IMAGE})` }}
      />
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-wine-950/95 via-wine-950/85 to-wine-950/95" />
      <div className="absolute inset-0 z-0 bg-grain pointer-events-none" />

      {/* Centered card */}
      <div className="relative z-10 w-full max-w-md px-6 py-16">
        <div className="flex flex-col items-center mb-10">
          <Link to="/" data-testid="login-logo-home">
            <img
              src={LOGO_STACKED}
              alt="Sixth Mount Zion"
              className="h-20 w-auto mb-6"
            />
          </Link>
          <h1 className="text-4xl md:text-5xl font-sans font-black tracking-tight text-center">
            Sign <em className="text-gold-light italic font-light">in</em>.
          </h1>
          <p className="text-sm text-white/65 mt-3 text-center max-w-xs">
            Access leadership resources, ministry media requests, and announcements.
          </p>
        </div>

        <form onSubmit={submit} className="glass p-10 space-y-7" data-testid="login-form">
          <label className="block">
            <div className="text-xs uppercase tracking-widest text-white/55 mb-2 font-semibold">Email</div>
            <input
              data-testid="login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-transparent border-0 border-b border-white/15 px-0 py-3 focus:outline-none focus:border-gold text-white"
            />
          </label>
          <label className="block">
            <div className="text-xs uppercase tracking-widest text-white/55 mb-2 font-semibold">Password</div>
            <input
              data-testid="login-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-transparent border-0 border-b border-white/15 px-0 py-3 focus:outline-none focus:border-gold text-white"
            />
          </label>
          <button
            type="submit"
            disabled={loading}
            className="btn-wine w-full py-3.5 rounded-full text-sm uppercase tracking-widest font-semibold disabled:opacity-50"
            data-testid="login-submit"
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>
          <div className="text-sm text-white/60 text-center">
            New here?{" "}
            <Link to="/register" className="text-gold hover:text-gold-light underline" data-testid="login-to-register">
              Create an account
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
